import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronRight, ChevronDown, Play, Star, Users, Calendar, 
  MapPin, Phone, Mail, Clock, Heart, Shield, Eye, Zap,
  Music, BookOpen, Award, ArrowRight, Crown, Waves
} from 'lucide-react';
import { Link } from 'react-router-dom';

const KapoeiraLandingInner = () => {
  const hero = useHero('capoeira');
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const benefits = [
    {
      icon: Eye,
      title: "Awareness & Visualization",
      description: "Develop keen awareness of your body's capabilities and visualize movements before execution."
    },
    {
      icon: Heart,
      title: "Tranquility & Control",
      description: "Cultivate calmness, patience, and complete control over mind and body."
    },
    {
      icon: Zap,
      title: "Cleverness & Deception",
      description: "Master the art of misdirection and strategic thinking within the roda."
    },
    {
      icon: Music,
      title: "Rhythm & Soul",
      description: "Express yourself through movement that comes from the heart, connected to the music."
    },
    {
      icon: Shield,
      title: "Freedom & Balance",
      description: "Achieve mobility and find balance within chaos, moving naturally and subtly."
    },
    {
      icon: Star,
      title: "Self-Preservation",
      description: "Learn objective techniques for protection while maintaining grace and fluidity."
    }
  ];

  const frogMovements = [
    { name: "The Jinga", desc: "The foundation - like a frog's subtle weight shifts before a leap", icon: Waves },
    { name: "Au (Cartwheel)", desc: "The frog's rolling escape through water", icon: Zap },
    { name: "Negativa", desc: "Low to the ground - the frog's defensive crouch", icon: Heart },
    { name: "Rasteira", desc: "The sweeping kick - a frog's powerful leg strike", icon: Star },
  ];

  const classSchedule = [
    { day: "Wednesday", time: "6:00 PM", type: "All Levels" },
    { day: "Friday", time: "6:00 PM", type: "All Levels" },
    { day: "Sunday", time: "10:00 AM - 12:00 PM", type: "Private Appointments Only" }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-black overflow-hidden">
      {/* ============================================ */}
      {/* HERO SECTION - Frog Style with Mestre Photo */}
      {/* ============================================ */}
      <section className="relative min-h-screen flex items-center justify-center bg-black">
        {/* Animated background particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-green-500/30 rounded-full"
              initial={{ 
                x: Math.random() * 100 + 'vw', 
                y: Math.random() * 100 + 'vh',
                scale: Math.random() * 2 + 0.5
              }}
              animate={{ 
                y: [null, Math.random() * -100 + 'vh'],
                opacity: [0.3, 0.8, 0.3]
              }}
              transition={{ 
                duration: 10 + Math.random() * 10,
                repeat: Infinity,
                ease: "linear"
              }}
            />
          ))}
        </div>
        
        <div className="max-w-7xl mx-auto px-6 py-20 relative z-10">
          {/* Hero Image - Blended Layout: Frog LEFT, Instruments RIGHT, Mestre CENTER */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="relative mb-12"
          >
            {/* Subtle glow effect */}
            <div className="absolute inset-0 bg-green-500/5 blur-3xl rounded-full" />
            
            {/* Main image container */}
            <div className="relative max-w-6xl mx-auto rounded-3xl overflow-hidden shadow-xl shadow-green-900/30">
            {/* Base image - Frog LEFT, Berimbaus, Rio + NOLA background */}
            <PageEditableImage
              path="hero.backgroundImage"
              fallback="https://www.casasamba.com/wp-content/uploads/2026/09/ba7e1b7f8460ffe017b50ce5a3cccb869293ab78a9fe6e79f15792f81f04ee11.png"
              alt="Ancient Frog Guardian - Rio meets New Orleans with Kapoeira Instruments"
              className="w-full"
            />
              
              {/* Mestre photo overlay - CENTERED */}
              <div className="absolute inset-0 flex items-end justify-center pointer-events-none">
                <PageEditableImage
                  path="hero.mestreImage"
                  fallback="https://www.casasamba.com/wp-content/uploads/2026/09/ygwclug5_Mestre-Avatar-ID-photo-2.png"
                  alt="Mestre Curtis Pierre"
                  className="h-[160%] w-auto max-w-[85%] object-contain object-bottom"
                />
              </div>
              
              {/* Dark overlay - ON TOP of everything to blend whole image */}
              <div className="absolute inset-0 bg-black/55 pointer-events-none" />
              
              {/* Floating crown/badge */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-green-500 to-emerald-600 text-black px-6 py-2 rounded-full font-black text-sm flex items-center gap-2 shadow-lg z-10"
              >
                <Crown className="w-4 h-4" />
                <PageEditable path="hero.crownBadge" fallback="THE FROG STYLE" />
              </motion.div>
            </div>
          </motion.div>
          
          {/* Text Content - Centered */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-full text-green-400 text-sm font-bold mb-6"
            >
              <Star className="w-4 h-4" />
              <PageEditable path="hero.eyebrow" fallback={hero.kicker || "1ST CLASS FREE - JOIN US NOW"} />
            </motion.div>
            
            <h1 className="text-5xl lg:text-7xl font-black mb-6">
              <span className="text-white"><span className="text-green-400">K</span>apoeira</span>
              <br />
              <span className="bg-gradient-to-r from-green-400 via-emerald-400 to-amber-400 bg-clip-text text-transparent">
                <PageEditable path="hero.titleAccent" fallback={hero.title_bottom || "Do Sapo"} />
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-4 max-w-3xl mx-auto">
              <PageEditable
                path="hero.subtitleItalic"
                fallback='"The Way of the Frog" — A revolutionary approach to Kapoeira Angola that honors the ancient connection between the frog and the martial art.'
                multiline
              />
            </p>
            
            <PageEditable
              path="hero.body"
              as="p"
              multiline
              className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto"
              fallback="Every Kapoeira movement mirrors the frog: the low stance of the Negativa, the explosive leap of the Au, the patient stillness before the strike. For 38 years, Mestre Curtis Pierre has preserved this sacred knowledge."
            />
            
            {/* Stats */}
            <div className="grid grid-cols-4 gap-6 mb-10 max-w-2xl mx-auto">
              {[
                { value: "38+", label: "Years" },
                { value: "10K+", label: "Students" },
                { value: "324", label: "Pages" },
                { value: "∞", label: "Legacy" }
              ].map((stat, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + idx * 0.1 }}
                  className="text-center"
                >
                  <div className="text-3xl lg:text-4xl font-black text-green-400">{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </motion.div>
              ))}
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/kapoeira-angola"
                className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-black font-bold text-lg rounded-full hover:shadow-lg hover:shadow-green-500/30 transition-all flex items-center gap-2"
              >
                Start Your Journey
                <ArrowRight className="w-5 h-5" />
              </Link>
              
              <Link
                to="/kapoeira-book"
                className="px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold text-lg rounded-full hover:shadow-lg hover:shadow-amber-500/30 transition-all flex items-center gap-2"
              >
                <BookOpen className="w-5 h-5" />
                Read the Book
              </Link>
            </div>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-green-500/50 rounded-full flex items-start justify-center p-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-green-500 rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* ============================================ */}
      {/* WHY "KAPOEIRA" WITH A K - EXPLANATION */}
      {/* ============================================ */}
      <section className="relative py-16 px-6 bg-gradient-to-b from-black to-green-950/10">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-green-900/30 via-green-800/20 to-green-900/30 border border-green-500/30 rounded-3xl p-8 md:p-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-2xl font-black text-black">K</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-black text-white">
                <PageEditable path="whyK.titlePre" fallback="Why " />
                <span className="text-green-400"><PageEditable path="whyK.titleLetter" fallback="K" /></span>
                <PageEditable path="whyK.titlePost" fallback="apoeira?" />
              </h2>
            </div>
            
            <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
              <PageEditable
                path="whyK.p1"
                as="p"
                multiline
                fallback="We spell it Kapoeira with a K — not a C — to honor the true roots of this martial art: the Kongo people of Central Africa."
              />
              <PageEditable
                path="whyK.p2"
                as="p"
                multiline
                fallback="In the Kongo language and culture, the letter K carries deep significance. The word 'Kongo' itself is spelled with a K, representing the ancient kingdom and its people who were forcibly brought to Brasil during the transatlantic slave trade."
              />
              <PageEditable
                path="whyK.p3"
                as="p"
                multiline
                fallback="This intentional spelling is an act of cultural reclamation — bringing closer awareness to the African origins of this martial art that was born from the resistance and resilience of enslaved Kongolese people in Brasil."
              />
              <PageEditable
                path="whyK.p4"
                as="p"
                multiline
                fallback="Today, researchers and practitioners in the Democratic Republic of Congo are uncovering more evidence about the original combat techniques, spiritual practices, and cultural traditions that gave birth to what we now call Kapoeira. By using the K, we acknowledge this living connection and support the ongoing work to restore this art to its rightful place in African heritage."
              />
            </div>
            
            <div className="mt-8 pt-6 border-t border-green-500/20">
              <p className="text-green-400 font-semibold italic text-center">
                <PageEditable path="whyK.quote" fallback='"The K is not just a letter — it is a bridge back to our ancestors."' />
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================================ */}
      {/* THE FROG CONNECTION SECTION */}
      {/* ============================================ */}
      <section className="relative py-20 px-6 bg-gradient-to-b from-green-950/10 to-green-950/20">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-green-400 font-bold text-sm tracking-wider">
              <PageEditable path="frog.eyebrow" fallback="THE SACRED CONNECTION" />
            </span>
            <h2 className="text-3xl lg:text-5xl font-black text-white mt-2">
              <PageEditable path="frog.titlePre" fallback="Why the " />
              <span className="text-green-400"><PageEditable path="frog.titleAccent" fallback="Frog" /></span>
              <PageEditable path="frog.titlePost" fallback="?" />
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <PageEditable
                path="frog.p1"
                as="p"
                multiline
                className="text-lg text-gray-300 leading-relaxed"
                fallback="In the Kongo traditions of Central Africa, the frog represents transformation, adaptability, and hidden power. The enslaved Africans who created Kapoeira in Brasil carried these symbols with them."
              />
              <PageEditable
                path="frog.p2"
                as="p"
                multiline
                className="text-lg text-gray-300 leading-relaxed"
                fallback="Watch a Capoeirista move: the low crouching stance, the explosive unpredictable leaps, the patient waiting before striking — these are the ways of the frog."
              />
              <PageEditable
                path="frog.p3"
                as="p"
                multiline
                className="text-lg text-gray-300 leading-relaxed"
                fallback="The berimbau's curved bow mimics the arc of a frog's leap. The atabaque's rhythms echo the calls across ancient lagoons. Kapoeira Do Sapo reconnects practitioners to these original roots."
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              {frogMovements.map((move, idx) => (
                <div 
                  key={idx}
                  className="bg-green-900/20 border border-green-500/20 rounded-2xl p-6 hover:border-green-500/50 transition-all"
                >
                  <move.icon className="w-8 h-8 text-green-400 mb-3" />
                  <h3 className="text-lg font-bold text-white mb-1">
                    <PageEditable path={`frog.move${idx}.name`} fallback={move.name} />
                  </h3>
                  <PageEditable
                    path={`frog.move${idx}.desc`}
                    as="p"
                    multiline
                    className="text-sm text-gray-400"
                    fallback={move.desc}
                  />
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="relative py-20 px-6 bg-black">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(34,197,94,0.1),transparent_70%)]" />
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center relative z-10"
        >
          <div className="text-6xl text-green-500/30 mb-4">"</div>
          <blockquote className="text-2xl lg:text-3xl text-white font-light italic mb-6">
            <PageEditable
              path="quote.text"
              multiline
              fallback="The frog waits in stillness, then moves with explosive power. This is Kapoeira. This is life. Learn to wait. Learn to leap."
            />
          </blockquote>
          <cite className="text-green-400 font-bold">
            <PageEditable path="quote.attribution" fallback="— Mestre Curtis Pierre" />
          </cite>
        </motion.div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-24 px-6 bg-gradient-to-b from-green-950/20 to-black">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              <PageEditable path="benefits.titlePre" fallback="Transform Your " />
              <span className="text-green-400"><PageEditable path="benefits.titleAccent" fallback="Mind & Body" /></span>
            </h2>
            <PageEditable
              path="benefits.subtitle"
              as="p"
              multiline
              className="text-gray-400 text-lg max-w-2xl mx-auto"
              fallback="These are some of the acquired benefits from practicing Kapoeira Angola"
            />
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group p-6 bg-white/5 border border-white/10 rounded-2xl hover:border-green-500/50 hover:bg-green-500/5 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <benefit.icon className="w-7 h-7 text-black" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  <PageEditable path={`benefits.item${idx}.title`} fallback={benefit.title} />
                </h3>
                <PageEditable
                  path={`benefits.item${idx}.description`}
                  as="p"
                  multiline
                  className="text-gray-400"
                  fallback={benefit.description}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section with Image */}
      <section className="relative py-24 px-6 overflow-hidden bg-black">
        <div className="absolute inset-0 bg-gradient-to-r from-green-900/10 to-amber-900/10" />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
                <PageEditable path="art.titlePre" fallback="The Art of " />
                <span className="text-green-400"><PageEditable path="art.titleAccent" fallback="Kapoeira Angola" /></span>
              </h2>
              <PageEditable
                path="art.p1"
                as="p"
                multiline
                className="text-gray-300 text-lg mb-6"
                fallback="Kapoeira Angola is one of the many unknown original martial arts out of Africa. It represents the Kapoeira inheritance from the KONGO — a practice that combines fighting, dance, acrobatics, and music into one fluid expression of freedom."
              />
              <PageEditable
                path="art.p2"
                as="p"
                multiline
                className="text-gray-400 mb-8"
                fallback="More than just physical training, Kapoeira Angola is a complete cultural immersion. You'll learn Portuguese songs, play traditional instruments like the berimbau, and become part of a global community connected by this ancient art form."
              />
              
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full">
                  <Users className="w-5 h-5 text-green-400" />
                  <span className="text-white"><PageEditable path="art.chip1" fallback="All Skill Levels" /></span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full">
                  <Music className="w-5 h-5 text-green-400" />
                  <span className="text-white"><PageEditable path="art.chip2" fallback="Live Music" /></span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full">
                  <Heart className="w-5 h-5 text-red-400" />
                  <span className="text-white"><PageEditable path="art.chip3" fallback="Community" /></span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square rounded-3xl overflow-hidden border-4 border-green-500/50 shadow-2xl shadow-green-500/30">
                <PageEditableImage
                  path="art.image"
                  fallback="https://www.casasamba.com/wp-content/uploads/2026/09/kh4kq51g_Kapoeira-cover-drawing-2026a1-scaled.png"
                  alt="Kapoeira Angola Movements"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating Elements */}
              <motion.div 
                className="absolute -bottom-6 -left-6 px-6 py-4 bg-green-500 rounded-2xl shadow-xl"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <div className="text-black font-bold text-2xl">
                  <PageEditable path="art.badgeNumber" fallback="38+" />
                </div>
                <div className="text-black/70 text-sm">
                  <PageEditable path="art.badgeLabel" fallback="Years Teaching" />
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Schedule Section */}
      <section className="relative py-24 px-6 bg-gradient-to-b from-black to-green-950/20">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              <PageEditable path="schedule.titlePre" fallback="Class " />
              <span className="text-green-400"><PageEditable path="schedule.titleAccent" fallback="Schedule" /></span>
            </h2>
            <PageEditable
              path="schedule.subtitle"
              as="p"
              multiline
              className="text-gray-400 text-lg"
              fallback="Join us for training sessions in the heart of New Orleans"
            />
          </motion.div>

          <div className="space-y-4">
            {classSchedule.map((cls, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="flex items-center justify-between p-6 bg-white/5 border border-white/10 rounded-2xl hover:border-green-500/50 transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <div className="text-white font-bold text-lg">{cls.day}</div>
                    <div className="text-gray-400">{cls.type}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-green-400 font-medium">
                  <Clock className="w-5 h-5" />
                  {cls.time}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <Link
              to="/kapoeira-angola"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-black font-bold text-lg rounded-full hover:shadow-lg hover:shadow-green-500/30 transition-all"
            >
              <Calendar className="w-5 h-5" />
              Book Your Free Class
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Book CTA Section */}
      <section className="relative py-24 px-6 overflow-hidden bg-black">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-black to-green-900/20" />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[3/4] max-w-sm mx-auto rounded-2xl overflow-hidden shadow-2xl shadow-amber-500/20 border-4 border-amber-500/30 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                <img loading="lazy" decoding="async" 
                  src="/api/media/casa-samba/library/images/capoeira-book-cover.jpg"
                  alt="Kapoeira Angola Movements Book"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-block px-4 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm font-medium mb-4">
                Study Guide Available
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
                Kapoeira Angola <span className="text-amber-400">Movements</span>
              </h2>
              <p className="text-gray-300 text-lg mb-6">
                A comprehensive 324-page study guide for Kapoeira Angola. Learn the movements, 
                understand the history, and master the techniques with detailed illustrations 
                by Mestre Curtis Pierre.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-gray-300">
                  <Star className="w-5 h-5 text-amber-400" />
                  324 Pages of Detailed Instructions
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <Star className="w-5 h-5 text-amber-400" />
                  Hand-drawn Illustrations
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <Star className="w-5 h-5 text-amber-400" />
                  From Beginner to Advanced
                </li>
              </ul>
              <Link
                to="/kapoeira-book"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold text-lg rounded-full hover:shadow-lg hover:shadow-amber-500/30 transition-all"
              >
                <BookOpen className="w-5 h-5" />
                Get the Book
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative py-24 px-6 bg-gradient-to-b from-black to-green-950/20">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              Begin Your <span className="text-green-400">Journey</span>
            </h2>
            <p className="text-gray-400 text-lg mb-12">
              Your first class is free. Come experience the transformative power of Kapoeira Angola.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
                <MapPin className="w-8 h-8 text-green-400 mx-auto mb-4" />
                <h3 className="text-white font-bold mb-2">Location</h3>
                <p className="text-gray-400">New Orleans, Louisiana</p>
              </div>
              <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
                <Mail className="w-8 h-8 text-green-400 mx-auto mb-4" />
                <h3 className="text-white font-bold mb-2">Email</h3>
                <p className="text-gray-400">info@casasamba.com</p>
              </div>
              <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
                <Phone className="w-8 h-8 text-green-400 mx-auto mb-4" />
                <h3 className="text-white font-bold mb-2">Phone</h3>
                <p className="text-gray-400">Contact for details</p>
              </div>
            </div>

            <Link
              to="/kapoeira-angola"
              className="inline-flex items-center gap-2 px-10 py-5 bg-gradient-to-r from-green-500 via-emerald-500 to-amber-500 text-black font-bold text-xl rounded-full hover:shadow-xl hover:shadow-green-500/30 transition-all"
            >
              Start Training Today
              <ArrowRight className="w-6 h-6" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-8 px-6 border-t border-white/10 bg-black">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-gray-400 text-sm">
            © {new Date().getFullYear()} Grupo Besouro Kapoeira Angola New Orleans. All rights reserved.
          </div>
          <div className="flex items-center gap-6">
            <Link to="/kapoeira-book" className="text-gray-400 hover:text-green-400 transition-colors">
              Study Guide
            </Link>
            <Link to="/classes" className="text-gray-400 hover:text-green-400 transition-colors">
              Classes
            </Link>
            <Link to="/legacy" className="text-gray-400 hover:text-green-400 transition-colors">
              About Mestre
            </Link>
            <Link to="/mestre-bullfrog" className="text-gray-400 hover:text-green-400 transition-colors">
              Frog Style
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { PageEditable } from '@/components/edit/PageEditable';
import { useHero } from '@/hooks/useHero';

import { PageEditableImage } from '@/components/edit/PageEditableImage';

const KapoeiraLanding = () => (
  <PageEditProvider slug="capoeira-landing">
    <KapoeiraLandingInner />
    <PageEditToolbar />
  </PageEditProvider>
);

export default KapoeiraLanding;
